import { useMemo } from "react";

import {
	liveSessionEnabled,
	normalizeProethicaSessionResponse,
} from "@constants/demo";
import {
	type ProethicaTestDescriptor,
	type ProethicaTestKind,
	proethicaTestDescriptors,
} from "@constants/demo/proethica-tests";
import { computeProethicaTestScore } from "@features/report/helpers/proethica-test-report";
import type { fetchSessionItems } from "@features/session/api/mutations";
import { sessionItemsQueryOptions } from "@features/session/api/queries";
import { resolveSummativeItemStates } from "@features/session/helpers/seed-answer-state";
import type { ItemStates } from "@features/session/hooks/types";
import {
	type ModuleSession,
	useProethicaSessionStore,
} from "@features/session/hooks/useProethicaSessionStore";
import { useProethicaTestItemStatesStore } from "@features/session/hooks/useProethicaTestItemStatesStore";
import { calculateScore } from "@helpers/exams";
import { flattenSessionItems } from "@helpers/session";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { useQueries } from "@tanstack/react-query";
import {
	type ProethicaExam,
	type ProethicaTestCompletionSignals,
	isProethicaTestComplete,
	resolveProethicaSessionNumericId,
} from "../helpers/proethica-module-list";

const EMPTY_EXAMS: ProethicaExam[] = [];

type TestScoreTarget = {
	collectionId: string;
	numericId: number;
	exam?: ProethicaExam;
};

const resolveTestScoreTargets = (
	descriptors: ProethicaTestDescriptor[],
	exams: ProethicaExam[],
	signals: ProethicaTestCompletionSignals,
	sessionByModule: Record<string, ModuleSession>,
): TestScoreTarget[] => {
	const examByCollectionId = new Map(
		exams.map((exam) => [String(exam.collectionId), exam]),
	);

	return descriptors.flatMap((test) => {
		const collectionId = String(test.collectionId);
		const exam = examByCollectionId.get(collectionId);
		if (!isProethicaTestComplete(test.kind, exam?.status, signals)) {
			return [];
		}

		const numericId = resolveProethicaSessionNumericId(
			exam,
			sessionByModule[collectionId],
		);
		if (numericId == null) {
			return [];
		}

		return [{ collectionId, numericId, exam }];
	});
};

const fallbackDistributionScore = (exam?: ProethicaExam): number | null => {
	if (!exam?.distribution) {
		return null;
	}
	if (!exam.numItems) {
		return null;
	}
	return calculateScore(exam.distribution, exam.numItems);
};

type SessionItemsResponse = Awaited<ReturnType<typeof fetchSessionItems>>;
type GetTestItemStates = (numericId: number) => ItemStates | undefined;

const computeScoreFromSession = (
	queryData: SessionItemsResponse,
	numericId: number,
	getTestItemStates: GetTestItemStates,
): number | null => {
	const response = normalizeProethicaSessionResponse(queryData);
	const sessionItems = flattenSessionItems(response.sessionItems);
	if (!sessionItems.length) {
		return null;
	}
	const itemStates = resolveSummativeItemStates(
		sessionItems,
		getTestItemStates(numericId),
	);
	return computeProethicaTestScore(sessionItems, itemStates).percentage;
};

const buildTestScores = (
	descriptors: ProethicaTestDescriptor[],
	exams: ProethicaExam[],
	signals: ProethicaTestCompletionSignals,
	targets: TestScoreTarget[],
	queries: { data?: SessionItemsResponse; isLoading: boolean }[],
	live: boolean,
	getTestItemStates: GetTestItemStates,
): Map<string, number> => {
	const scores = new Map<string, number>();
	const examByCollectionId = new Map(
		exams.map((exam) => [String(exam.collectionId), exam]),
	);
	const targetByCollectionId = new Map(
		targets.map((target, index) => [
			target.collectionId,
			{ target, query: queries[index] },
		]),
	);

	for (const test of descriptors) {
		const collectionId = String(test.collectionId);
		const exam = examByCollectionId.get(collectionId);
		if (!isProethicaTestComplete(test.kind, exam?.status, signals)) {
			continue;
		}

		const entry = targetByCollectionId.get(collectionId);
		const { target, query } = entry ?? {};

		if (target && live && query?.isLoading) {
			continue;
		}

		let score: number | null = null;

		if (query?.data && live && target) {
			score = computeScoreFromSession(
				query.data,
				target.numericId,
				getTestItemStates,
			);
		}

		if (score == null) {
			score = fallbackDistributionScore(exam);
		}

		if (score != null) {
			scores.set(collectionId, score);
		}
	}

	return scores;
};

type TestScoreOptions = {
	enabled?: boolean;
	kind?: ProethicaTestKind;
};

export const useProethicaTestScores = (
	exams: ProethicaExam[] | undefined,
	signals: ProethicaTestCompletionSignals,
	{ enabled = true, kind }: TestScoreOptions = {},
) => {
	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	const sessionByModule = useProethicaSessionStore(
		(state) => state.sessionByModule,
	);
	const getTestItemStates = useProethicaTestItemStatesStore(
		(state) => state.getTestItemStates,
	);
	const live = liveSessionEnabled(demoExamId);
	const examList = exams ?? EMPTY_EXAMS;
	// Callers pass signals as an inline literal; memoizing on the booleans keeps
	// the memos below from recomputing every render.
	const { preTestCompleted, postTestPassed } = signals;
	const stableSignals = useMemo(
		() => ({ preTestCompleted, postTestPassed }),
		[preTestCompleted, postTestPassed],
	);

	const descriptors = useMemo(
		() =>
			enabled
				? proethicaTestDescriptors.filter(
						(test) => kind == null || test.kind === kind,
					)
				: [],
		[enabled, kind],
	);

	const targets = useMemo(
		() =>
			resolveTestScoreTargets(
				descriptors,
				examList,
				stableSignals,
				sessionByModule,
			),
		[descriptors, examList, stableSignals, sessionByModule],
	);

	// Without combine, useQueries returns a new array every render, defeating
	// the testScores memo; the combined result is structurally shared.
	const { snapshots, isLoading } = useQueries({
		queries: targets.map((target) => ({
			...sessionItemsQueryOptions(target.numericId),
			enabled: live && target.numericId > 0,
		})),
		combine: (results) => ({
			snapshots: results.map(({ data, isLoading }) => ({ data, isLoading })),
			isLoading: results.some((result) => result.isLoading),
		}),
	});

	const testScores = useMemo(
		() =>
			buildTestScores(
				descriptors,
				examList,
				stableSignals,
				targets,
				snapshots,
				live,
				getTestItemStates,
			),
		[
			descriptors,
			examList,
			stableSignals,
			targets,
			snapshots,
			live,
			getTestItemStates,
		],
	);

	return { testScores, isLoading };
};
